// =============================================================================
// SHANIZ GAMING ADMIN - FRAUD & RISK DASHBOARD
// =============================================================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Warning as WarningIcon, Flag as FlagIcon, PersonOutline } from '@mui/icons-material';
import { fraudService, FraudAlert, RiskDevice, IpLoginEvent, RiskUser, extractList } from '../services/api';
import { PermissionButton } from '../components/PermissionButton';
import { useAdminSocket } from '../hooks/useAdminSocket';
import { chartTokens, tooltipProps } from '../theme/chartTheme';

dayjs.extend(relativeTime);

const SeverityChip: React.FC<{ severity: string | number | null | undefined }> = ({ severity }) => {
  // Backend may send severity as an int (0-3) or a string (low/medium/high/critical).
  const asString = (() => {
    if (severity == null) return 'UNKNOWN';
    if (typeof severity === 'number') {
      if (severity >= 3) return 'CRITICAL';
      if (severity >= 2) return 'HIGH';
      if (severity >= 1) return 'MEDIUM';
      return 'LOW';
    }
    return String(severity).toUpperCase();
  })();
  const colorMap: Record<string, 'default' | 'info' | 'warning' | 'error' | 'success'> = {
    LOW: 'success', INFO: 'info', MEDIUM: 'warning', WARNING: 'warning',
    HIGH: 'error', ERROR: 'error', CRITICAL: 'error', UNKNOWN: 'default',
  };
  return <Chip size="small" color={colorMap[asString] || 'default'} label={asString} />;
};

const FraudRisk: React.FC = () => {
  const theme = useTheme();
  const ct = chartTokens(theme);
  const tt = tooltipProps(theme);
  const qc = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<RiskUser | null>(null);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });
  const feedEnd = useRef<HTMLDivElement | null>(null);

  const overview = useQuery({ queryKey: ['risk', 'overview'], queryFn: fraudService.getOverview });

  const riskUsers = useQuery({
    queryKey: ['risk', 'users'],
    queryFn: () => fraudService.getRiskUsers({ page: 1, limit: 30 }),
  });

  const alerts = useInfiniteQuery({
    queryKey: ['risk', 'alerts'],
    queryFn: ({ pageParam }) => fraudService.getAlerts(pageParam as string | undefined, 25),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });

  const alertItems = useMemo<FraudAlert[]>(() => alerts.data?.pages.flatMap((p) => p.items) || [], [alerts.data]);

  const userDetail = useQuery({
    queryKey: ['risk', 'user', selectedUser?.userId],
    queryFn: () => fraudService.getUserRisk(selectedUser!.userId),
    enabled: Boolean(selectedUser?.userId),
  });

  const flag = useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) => fraudService.flagUser(userId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['risk'] });
      setFlagOpen(false);
      setFlagReason('');
      setSnackbar({ open: true, msg: 'User flagged', sev: 'success' });
    },
    onError: (e: Error) => setSnackbar({ open: true, msg: e.message || 'Flag failed', sev: 'error' }),
  });

  // Live prepend when WebSocket pushes a new alert
  useAdminSocket((ev) => {
    if (ev.type === 'fraud.alert') {
      qc.setQueryData(['risk', 'alerts'], (old: any) => {
        if (!old || !('pages' in old) || !Array.isArray(old.pages) || !old.pages.length) return old;
        const first = old.pages[0];
        const newFirst = { ...first, items: [ev.payload as FraudAlert, ...(first.items || [])] };
        return { ...old, pages: [newFirst, ...old.pages.slice(1)] };
      });
      qc.invalidateQueries({ queryKey: ['risk', 'overview'] });
    }
  });

  // Infinite scroll observer
  useEffect(() => {
    const el = feedEnd.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && alerts.hasNextPage && !alerts.isFetchingNextPage) {
        alerts.fetchNextPage();
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [alerts]);

  // Backend rows are `{ level, count }`; chart XAxis expects `bucket`. Normalise.
  const distribution = (Array.isArray(overview.data?.distribution) ? overview.data!.distribution : []).map((d: any) => ({
    bucket: d?.bucket ?? d?.level ?? d?.label ?? '—',
    count: Number(d?.count) || 0,
  }));

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Fraud & Risk
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'High-risk users', value: overview.data?.highRiskUsers, color: 'error.main' },
          { label: 'Active alerts', value: overview.data?.activeAlerts, color: 'warning.main' },
          // Backend returns `flaggedTransactions24h`; older builds used `flagged24h`.
          { label: 'Flagged txns (24h)', value: overview.data?.flagged24h ?? (overview.data as any)?.flaggedTransactions24h, color: 'info.main' },
        ].map((s) => (
          <Grid item xs={12} md={4} key={s.label}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
                <Typography variant="h4" fontWeight={700} color={s.color}>
                  {overview.isLoading ? <Skeleton width={80} /> : s.value ?? 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Risk score distribution
              </Typography>
              <Box sx={{ height: 260 }}>
                {overview.isLoading ? (
                  <Skeleton variant="rectangular" height={260} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={distribution}>
                      <CartesianGrid strokeDasharray="3 3" stroke={ct.grid} />
                      <XAxis dataKey="bucket" stroke={ct.axisTick} />
                      <YAxis stroke={ct.axisTick} />
                      <RTooltip {...tt} />
                      <Bar dataKey="count" fill={theme.palette.error.main} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Risk users
              </Typography>
              {riskUsers.isLoading && <Skeleton variant="rectangular" height={200} />}
              {riskUsers.isError && <Alert severity="error">Failed to load risk users.</Alert>}
              <List dense>
                {/* Backend risk/users rows: { userId, score, level, user: { username } }.
                    Some legacy rows use riskScore/riskLevel/username directly. Normalise. */}
                {extractList<any>(riskUsers.data).map((raw) => {
                  const u: RiskUser = {
                    userId: raw.userId ?? raw.user?.id ?? raw.id ?? '',
                    username: raw.username ?? raw.user?.username ?? raw.userId?.slice?.(0, 8) ?? 'unknown',
                    riskScore: Number(raw.riskScore ?? raw.score ?? 0),
                    riskLevel: (raw.riskLevel ?? raw.level ?? 'LOW') as RiskUser['riskLevel'],
                    flags: Array.isArray(raw.flags) ? raw.flags : [],
                    lastActivity: raw.lastActivity ?? raw.lastComputedAt ?? raw.updatedAt ?? '',
                    activeAlerts: Number(raw.activeAlerts ?? 0),
                    totalFlagged: Number(raw.totalFlagged ?? 0),
                  };
                  return (
                    <ListItem
                      key={u.userId || raw.id}
                      secondaryAction={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            label={`score ${u.riskScore}`}
                            color={u.riskLevel === 'CRITICAL' ? 'error' : u.riskLevel === 'HIGH' ? 'warning' : 'default'}
                          />
                          <Button size="small" variant="outlined" onClick={() => setSelectedUser(u)}>
                            Open
                          </Button>
                        </Stack>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar><PersonOutline /></Avatar>
                      </ListItemAvatar>
                      <ListItemText primary={u.username} secondary={u.flags.length ? u.flags.join(', ') : 'no flags'} />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Live alerts feed
              </Typography>
              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 520 }}>
                {alerts.isLoading && <Skeleton variant="rectangular" height={320} />}
                {alerts.isError && <Alert severity="error">Failed to load alerts.</Alert>}
                <List dense>
                  {alertItems.map((a) => {
                    // Backend alert rows lack `title`/`description`; the kind + metadata
                    // evidence are what's actually present. Coerce everything to strings
                    // so SeverityChip and dayjs never explode on int/null values.
                    const meta = (a.metadata ?? {}) as Record<string, unknown>;
                    const title = (a as any).title || a.kind || 'Fraud alert';
                    const description = (a as any).description || (meta.evidence as string) || '';
                    const severity = a.severity;
                    // For avatar color we only care about the tier, not the raw value.
                    const severityStr = typeof severity === 'string' ? severity.toLowerCase() : '';
                    const avatarBg = severityStr === 'critical' || severityStr === 'error' || (typeof severity === 'number' && severity >= 3)
                      ? 'error.main'
                      : 'warning.main';
                    return (
                      <ListItem key={a.id} alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: avatarBg }}>
                            <WarningIcon fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" fontWeight={600}>
                                {title}
                              </Typography>
                              <SeverityChip severity={severity} />
                            </Stack>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" color="text.secondary" component="span">
                                {a.kind || ''} · {a.username || a.userId || '—'} · {a.createdAt ? (dayjs(a.createdAt).fromNow?.() || dayjs(a.createdAt).format('MMM D HH:mm')) : ''}
                              </Typography>
                              {description && (
                                <>
                                  <br />
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    {description}
                                  </Typography>
                                </>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
                <div ref={feedEnd} />
                {alerts.isFetchingNextPage && <Skeleton height={40} />}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* User risk detail dialog */}
      <Dialog open={Boolean(selectedUser)} onClose={() => setSelectedUser(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Risk profile — {selectedUser?.username}
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Devices" />
            <Tab label="IP history" />
            <Tab label="Risk events" />
          </Tabs>
          {userDetail.isLoading && <Skeleton variant="rectangular" height={200} />}
          {tab === 0 && (
            <List dense>
              {(Array.isArray(userDetail.data?.devices) ? userDetail.data!.devices : []).map((d: RiskDevice) => (
                <ListItem
                  key={d.id}
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      {d.trusted && <Chip size="small" color="success" label="Trusted" />}
                      {d.flagged && <Chip size="small" color="error" label="Flagged" />}
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={`${d.platform} · ${d.model || d.deviceId}`}
                    secondary={`${d.ipAddress || ''} · ${d.geolocation?.city || ''} ${d.geolocation?.country || ''} · last seen ${dayjs(d.lastSeenAt).format('MMM D HH:mm')}`}
                  />
                </ListItem>
              ))}
              {!userDetail.isLoading && (Array.isArray(userDetail.data?.devices) ? userDetail.data!.devices : []).length === 0 && (
                <Alert severity="info">No devices recorded.</Alert>
              )}
            </List>
          )}
          {tab === 1 && (
            <List dense>
              {/* ipHistory is a legacy field; backend no longer returns it. Guard with Array.isArray. */}
              {(Array.isArray(userDetail.data?.ipHistory) ? userDetail.data!.ipHistory : []).map((ev: IpLoginEvent) => (
                <ListItem key={ev.id}>
                  <ListItemText
                    primary={`${ev.ipAddress} ${ev.successful ? '' : '(failed)'}`}
                    secondary={`${ev.city || ''} ${ev.country || ''} · ${dayjs(ev.at).format('MMM D HH:mm')}`}
                  />
                </ListItem>
              ))}
              {!userDetail.isLoading && (Array.isArray(userDetail.data?.ipHistory) ? userDetail.data!.ipHistory : []).length === 0 && (
                <Alert severity="info">No login history.</Alert>
              )}
            </List>
          )}
          {tab === 2 && (
            <List dense>
              {(Array.isArray(userDetail.data?.events) ? userDetail.data!.events : []).map((ev: FraudAlert) => (
                <ListItem key={ev.id}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {(ev as any).title || ev.kind || 'Risk event'}
                        </Typography>
                        <SeverityChip severity={ev.severity} />
                      </Stack>
                    }
                    secondary={`${ev.kind || ''} · ${ev.createdAt ? dayjs(ev.createdAt).format('MMM D HH:mm') : ''}`}
                  />
                </ListItem>
              ))}
              {!userDetail.isLoading && (Array.isArray(userDetail.data?.events) ? userDetail.data!.events : []).length === 0 && (
                <Alert severity="info">No risk events.</Alert>
              )}
            </List>
          )}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Last activity
            </Typography>
            <Typography variant="body2">
              {(() => {
                const rd: any = userDetail.data || {};
                const ts = rd.user?.lastActivity ?? rd.score?.lastComputedAt ?? rd.lastActivity;
                return ts ? dayjs(ts).format('MMM D, YYYY HH:mm') : '—';
              })()}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedUser(null)}>Close</Button>
          <PermissionButton
            permission="fraud.flag"
            color="error"
            variant="contained"
            startIcon={<FlagIcon />}
            onClick={() => setFlagOpen(true)}
          >
            Flag account
          </PermissionButton>
        </DialogActions>
      </Dialog>

      <Dialog open={flagOpen} onClose={() => setFlagOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Flag account</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Reason"
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!flagReason.trim() || flag.isPending || !selectedUser}
            onClick={() => selectedUser && flag.mutate({ userId: selectedUser.userId, reason: flagReason.trim() })}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.sev} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FraudRisk;
